import browseCategoriesTree from '../config/event-browse-categories.json';
import browseSportsSubcategories from '../config/browse-sports-subcategories.json';
import browsePerformerKeywords from '../config/browse-performer-keywords.json';
import {
  PRIMARY_EVENT_SHARETRIBE_ENUM_PUBLIC_DATA_KEY,
  PROMOTIX_EVENT_TYPE_CATALOG,
  SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID,
} from '../config/primaryEventDiscovery';
import {
  keywordPatternsFromConfig,
  keywordPatternsToSearchParam,
  primaryKeywordForSearchUrl,
} from './browseCategoryKeywords';
import { flattenBrowseCategoryConfig } from './flattenBrowseCategoryConfig';

const mergeChildPerformers = child => {
  if (!child?.slug) {
    return child;
  }
  const extra = browsePerformerKeywords[child.slug];
  if (!Array.isArray(extra) || !extra.length) {
    return child;
  }
  const base = keywordPatternsFromConfig(child.keywords);
  const seen = new Set(base);
  const merged = [...base];
  for (const k of extra) {
    const n = String(k).toLowerCase().trim();
    if (n && !seen.has(n)) {
      seen.add(n);
      merged.push(n);
    }
  }
  return { ...child, keywords: merged };
};

const browseTreeForSlugLookup = browseCategoriesTree.map(node => {
  let next = node;
  if (node?.slug === 'sports') {
    if (!Array.isArray(node.children) || !node.children.length) {
      next = { ...node, children: browseSportsSubcategories };
    }
  }
  if (Array.isArray(next.children) && next.children.length) {
    next = { ...next, children: next.children.map(mergeChildPerformers) };
  }
  return next;
});

/**
 * Public data key used in search URLs: `pub_<key>=<enum option value>`.
 * Must match an indexed `publicData` field in Flex with enum options aligned to
 * `SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID` (used for discovery primary events).
 * Override in primaryEventDiscovery with `PRIMARY_EVENT_SHARETRIBE_ENUM_PUBLIC_DATA_KEY`, or set
 * server env `EVENT_BROWSE_PUBLIC_DATA_KEY` for HTTP 301 redirects only.
 */
export const getEventBrowsePublicDataKey = () =>
  PRIMARY_EVENT_SHARETRIBE_ENUM_PUBLIC_DATA_KEY || 'category';

export const getEventBrowseQueryParamName = () =>
  `pub_${getEventBrowsePublicDataKey()}`;

/** Static tree for UI fallback (inventory API supplies live children). */
export const EVENT_BROWSE_CATEGORY_TREE = browseCategoriesTree;

/** Flat list of every browsable slug (parents + children, including sports subs). */
export const EVENT_BROWSE_CATEGORIES = flattenBrowseCategoryConfig(browseTreeForSlugLookup);

const slugToEntry = (() => {
  const m = new Map();
  for (const row of EVENT_BROWSE_CATEGORIES) {
    if (row && row.slug && row.enumOption) {
      m.set(String(row.slug).toLowerCase(), row);
    }
  }
  return m;
})();

export const getBrowseCategoryBySlug = slug => {
  if (slug == null || slug === '') {
    return null;
  }
  return slugToEntry.get(String(slug).toLowerCase()) || null;
};

export const buildBrowseCategorySearchParams = entry => {
  if (!entry || !entry.enumOption) {
    return {};
  }
  const patterns = keywordPatternsFromConfig(entry.keywords);
  const base = { page: 1 };

  if (patterns.length) {
    // Subcategory browse: server applies full keyword rules via browseSub. Do not also set
    // pub_category — TN ingest and primary event_type_id often disagree with the browse enum.
    base.browseSub = entry.slug;
    base.keywords = primaryKeywordForSearchUrl(entry.keywords) || patterns[0];
    return base;
  }

  base[getEventBrowseQueryParamName()] = entry.enumOption;
  return base;
};

/** Display label: explicit `label`, else PromoTix event type catalog, else slug. */
export const getBrowseCategoryDisplayLabel = entry => {
  if (!entry) {
    return '';
  }
  if (entry.label != null && String(entry.label).trim() !== '') {
    return String(entry.label).trim();
  }
  const typeId = SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID[entry.enumOption];
  if (typeId != null && PROMOTIX_EVENT_TYPE_CATALOG[typeId]) {
    return PROMOTIX_EVENT_TYPE_CATALOG[typeId];
  }
  return entry.slug;
};
